import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { routes } from "../router/routes";
import { strings } from "../resources/strings";

/** What every route was called before this existed. */
const APP_NAME = "RePath";

/**
 * Sets `document.title` per route — WCAG 2.1 SC 2.4.2 Page Titled.
 *
 * Every route previously shared the static `<title>RePath</title>` from
 * `public/index.html`, so a participant with several tabs open, or using a
 * screen reader's window list, could not tell them apart.
 *
 * Titles come from `routes[*].name`, which already exists and already feeds the
 * navbar — one source of truth, so a new route cannot quietly ship untitled.
 * The unknown-route case gets its own title rather than falling through to the
 * bare app name: landing somewhere unexpected is exactly when a participant
 * most needs the page to say what it is.
 */
const routeForPath = (pathname: string) =>
  Object.values(routes).find((r) => matchPath(r.path, pathname));

export const titleForPath = (pathname: string): string => {
  const name = routeForPath(pathname)?.name ?? strings.pageNotFound;
  return `${name} | ${APP_NAME}`;
};

export const useDocumentTitle = (): void => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = titleForPath(pathname);
  }, [pathname]);
};
